import * as React from 'react';
import { styled } from '@linaria/react';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function KeyframeGraph({ children, ...delegated }: Props) {
  return (
    <Graph {...delegated}>
      <Axes />
      <OpacityLabel>Opacity</OpacityLabel>
      <TimeLabel>Time</TimeLabel>
      <VerticalTicks>
        {['0', '0.25', '0.5', '0.75', '1'].map((value) => (
          <Item key={value}>
            <VerticalTick />
            <TickLabel>{value}</TickLabel>
          </Item>
        ))}
      </VerticalTicks>
      {children}
    </Graph>
  );
}

const Graph = styled.div`
  --axis-thickness: 5px;
  --tick-thickness: 3px;

  position: relative;
  height: 150px;
  width: 500px;
  max-width: 100%;
  margin-inline: auto;
  background: repeating-linear-gradient(
    to top,
    transparent 0,
    transparent 36.5px,
    hsl(0deg 0% 100% / 0.25) 36.5px 37.5px
  );
`;

const Axes = styled.div`
  position: absolute;
  inset: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: var(--color-text);
    border-radius: 1000px;
  }
  &::before {
    top: calc(var(--axis-thickness) * -1);
    left: 0;
    width: var(--axis-thickness);
    height: calc(100% + var(--axis-thickness) * 2);
    transform: translateX(-100%);
  }

  &::after {
    left: calc(var(--axis-thickness) * -1);
    bottom: 0;
    width: calc(100% + var(--axis-thickness));
    height: var(--axis-thickness);
    transform: translateY(100%);
  }
`;

const VerticalTicks = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: calc(150px + var(--tick-thickness));
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  transform: translate(calc(-100% - var(--axis-thickness)), 0);

  @media (max-width: 40rem) {
    display: none;
  }
`;

const Label = styled.div`
  position: absolute;
  font-size: 0.825rem;
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-mono);
  text-transform: uppercase;
  letter-spacing: 0.2em;
  opacity: 0.7;
`;

const OpacityLabel = styled(Label)`
  top: 0;
  left: -40px;
  bottom: 0;
  height: fit-content;
  margin-block: auto;
  transform: translateX(-100%) rotate(-90deg);

  @media (max-width: 45rem) {
    display: none;
  }
`;

const TimeLabel = styled(Label)`
  left: 0;
  right: 0;
  bottom: -40px;
  width: fit-content;
  margin-inline: auto;
`;

const Item = styled.div`
  position: relative;
`;

const VerticalTick = styled.div`
  display: block;
  width: 8px;
  height: var(--tick-thickness);
  background: var(--color-text);
  border-radius: 1000px 0 0 1000px;
  transform: translateY(50%);
`;

const TickLabel = styled.div`
  position: absolute;
  right: 12px;
  bottom: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  transform: translateY(50%);
`;

export default KeyframeGraph;
